import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const headerStyle = { 
 backgroundColor: "#92D050", 
 color: "#000", 
 fontWeight: "bold", 
 textAlign: "center", 
 verticalAlign: "middle", 
 border: "1px solid #000" 
};

const inputStyle = { 
 fontSize: "12px", 
 padding: "8px", 
 height: "auto", 
 minWidth: "80px",
 backgroundColor: "#fff"
};

const selectStyles = {
 menu: (provided) => ({ ...provided, zIndex: 9999 }),
 menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Moved outside to prevent re-renders on every keystroke
const EditableHeader = ({ label, keyName, rowSpan = 1, colSpan = 1, style = {}, onRemove = null, updateCustomHeader, isApproved = false }) => {
 return (
 <th 
 style={{ 
 ...headerStyle, 
 ...style, 
 position: "relative", 
 minWidth: colSpan > 1 ? "150px" : "100px",
 padding: "8px 12px",
 backgroundColor: "#92D050 !important" // Ensure green background
 }} 
 rowSpan={rowSpan} 
 colSpan={colSpan}
 >
 <div className="d-flex align-items-center justify-content-center w-100 h-100 position-relative group-header-container">
 <input
 type="text"
 className="form-control form-control-sm text-center fw-bold p-0 border-0 header-editable-input"
 style={{ 
 backgroundColor: "transparent", 
 color: "inherit", 
 fontSize: "12px",
 width: "calc(100% - 25px)",
 outline: "none",
 textTransform: "uppercase",
 minHeight: "30px",
 border: "1px solid transparent",
 borderRadius: "4px",
 cursor: isApproved ? "default" : "text"
 }}
 readOnly={isApproved}
 value={label}
 onChange={(e) => updateCustomHeader(keyName, e.target.value)}
 placeholder="Header"
 />
 {onRemove && !isApproved && (
 <button 
 type="button"
 className="btn btn-link text-danger p-0 border-0 remove-header-btn"
 style={{ 
 position: "absolute",
 right: "-6px",
 top: "50%",
 transform: "translateY(-50%)",
 fontSize: "18px",
 lineHeight: 1,
 textDecoration: "none",
 zIndex: 10,
 cursor: "pointer",
 background: "white",
 borderRadius: "50%",
 width: "20px",
 height: "20px",
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
 }}
 onClick={(e) => {
 e.stopPropagation();
 e.preventDefault();
 onRemove();
 }}
 >
 <i className="fa fa-times-circle"></i>
 </button>
 )}
 </div>
 </th>
 );
};

const DRAFT_KEY = "ms_add_draft";

const MeasurementSheetAdd = () => {
 const fallbackSheetTypes = [
 { code: "ELECTRICAL_LIGHTING", name: "Electrical Lighting" },
 { code: "CONDUIT_MEASUREMENT", name: "Conduit Measurement" },
 { code: "CONDUIT_SUMMARY", name: "Conduit Summary" },
 { code: "SWITCH_SOCKET", name: "Switch & Socket" },
 { code: "DB_INSTALLATION", name: "DB Installation" },
 { code: "CABLE_MEASUREMENT", name: "Cable Measurement" },
 { code: "CABLE_TRAY_SUMMARY", name: "Cable Tray Summary" },
 { code: "WIRING_MEASUREMENT", name: "Wiring Measurement" },
 ];

 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";

 const [loading, setLoading] = useState(false);
 const [isAutoSaving, setIsAutoSaving] = useState(false);
 const [savedId, setSavedId] = useState(null);
 const savedIdRef = useRef(null);
 const isSavingRef = useRef(false);
 const lastAutoSaveSignatureRef = useRef("");
 const [workOrders, setWorkOrders] = useState([]);
 const [sheetTypeMasters, setSheetTypeMasters] = useState([]);
 const [boqList, setBoqList] = useState([]);
 const [uomList, setUomList] = useState([]);
 const [usedQtyMap, setUsedQtyMap] = useState({});

 const [formData, setFormData] = useState({
 project: "",
 workOrder: "",
 boqItem: [],
 sheetType: "ELECTRICAL_LIGHTING",
 location: "",
 area: "",
 package: "ELECTRICAL INSTALLATION",
 preparedDate: new Date().toISOString().split("T")[0],
 certifiedDate: "",
 status: "Draft",
 preparedByName: "CANSCORP ENGINEERS PVT. LTD.",
 checkedByName: "CLIENT",
 itemName: "",
 description: "",
 unit: "",
 approvedQty: 0,
 usedQtyBefore: 0,
 remainingQtyBefore: 0,
 calculatedQty: 0,
 installedQty: 0,
 wlino: "",
 hsnSac: "",
 customHeaders: {},
 columnSettings: {},
 });

 const [columnConfig, setColumnConfig] = useState({});
 const [showModal, setShowModal] = useState(false);
 const [modalData, setModalData] = useState({ parent: "", subs: [""], fieldType: "number" });

 const [rows, setRows] = useState(Array.from({ length: 8 }, (_, i) => ({
 srNo: i + 1, location: "", uom: "MTR", wireSize: "", circuitNumber: "", conduitSize: "", pointFrom: "", pointTo: "", pointQty: 0, powerFrom: "", powerTo: "", powerQty: 0, subMainFrom: "", subMainTo: "", subMainQty: 0,
 primaryPointDbToSb: 0, primaryPointDbTo1stLight: 0, primaryPointSbTo1stPoint: 0, secondaryPointSbToSb: 0, secondaryPointPointToPoint: 0, primaryPowerPoints: 0, secondaryPowerPoints: 0,
 col1: 0, col2: 0, col3: 0, col4: 0, col5: 0, col6: 0, col7: 0, col8: 0, col9: 0, col10: 0, col11: 0, col12: 0, col13: 0, col14: 0, col15: 0, col16: 0, col17: 0, col18: 0, col19: 0, col20: 0,
 conduit20mm: 0, conduit25mm: 0, conduit32mm: 0, elvConduit20mm: 0, elvConduit25mm: 0, elvConduit32mm: 0,
 length: 0, run: 0, tray300mm: 0, tray450mm: 0, tray600mm: 0, ladder900mm: 0, ladder1200mm: 0, strip25x3: 0, strip32x6: 0, tray150mm: 0, tray100mm: 0, fanFixing: 0, lightFixing: 0, remarks: ""
 })));

 const defaultHeadersMap = {
 common: {
 srNo: "SR.NO", location: "LOCATION", uom: "UOM", from: "FROM", to: "TO", wireSize: "WIRE SIZES", circuitNo: "CIRCUIT NO.", conduitSize: "CONDUIT SIZE", remarks: "REMARKS", action: "ACTION",
 dbToSb: "DB TO SB", dbTo1stLight: "DB TO 1ST LIGHT", sbTo1stPoint: "SB TO 1ST POINT", sbToSb: "SB TO SB", pointToPoint: "POINT TO POINT", qty: "QTY", description: "DESCRIPTION / LOCATION", areaNo: "AREA NO.",
 pointFrom: "FROM", pointTo: "TO", pointQty: "QTY", powerFrom: "FROM", powerTo: "TO", powerQty: "QTY", subMainFrom: "FROM", subMainTo: "TO", subMainQty: "QTY"
 },
 ELECTRICAL_LIGHTING: { pointHeader: "LIGHT / FAN / EXHAUST POINT", powerHeader: "POWER POINTS", subMainHeader: "SUB MAINS" },
 CONDUIT_MEASUREMENT: { primaryHeader: "PRIMARY LIGHT/FAN/EXHAUST POINT", secondaryHeader: "SECONDARY POINTS", primaryPowerHeader: "PRIMARY POWER POINTS", secondaryPowerHeader: "SECONDARY POWER POINTS" },
 CONDUIT_SUMMARY: { conduitHeader: "LIGHT/FAN/EXHAUST, POWER POINT & SUB MAIN CONDUIT", elvConduitHeader: "ELV CONDUIT", col1: "20 MM", col2: "25 MM", col3: "32 MM", col4: "20 MM", col5: "25 MM", col6: "32 MM" },
 SWITCH_SOCKET: { col1: "6 AMP SWITCH", col2: "16 AMP SWITCH", col3: "6 AMP SOCKET", col4: "16 AMP SOCKET", col5: "1 MODULE", col6: "2 MODULE", col7: "3 MODULE", col8: "4 MODULE", col9: "6 MODULE", col10: "8 MODULE" },
 DB_INSTALLATION: { col1: "8 Way TPN DB", col2: "6 Way TPN DB", col3: "4 Way TPN DB", col4: "12 Way SPN DB", col5: "8 Way SPN DB", col6: "6 Way SPN DB", col7: "4 Way SPN DB", col8: "12 WAY VTPN" },
 CABLE_MEASUREMENT: { cableRunHeader: "CABLE RUN", cableQtyHeader: "CABLE QTY.", totalCablesHeader: "TOTAL CABLES" },
 CABLE_TRAY_SUMMARY: { lengthHeader: "LENGTH", runHeader: "RUN", perforated1Header: "PERFORATED", ladderHeader: "LADDER TRAY", stripHeader: "G.I STRIP", perforated2Header: "PERFORATED", tray300mm: "300 MM", tray450mm: "450 MM", tray600mm: "600 MM", ladder900mm: "900 MM", ladder1200mm: "1200 MM", strip25x3: "(25x3)MM GI", strip32x6: "(32x6)MM GI", tray150mm: "150 MM", tray100mm: "100 MM" },
 WIRING_MEASUREMENT: { primaryHeader: "PRIMARY LIGHT/FAN/EXHAUST POINT", secondaryHeader: "SECONDARY POINTS", primaryPowerHeader: "PRIMARY POWER POINTS", secondaryPowerHeader: "SECONDARY POWER POINTS", fanFixingHeader: "CEILING FAN FIXING", lightFixingHeader: "36 W. TUBE LIGHTS FIXING" }
 };

 const getHeaderLabel = (type, key, defaultLabel) => {
 const master = sheetTypeMasters.find((item) => item.code === type);
 const layout = getMasterLayout(master);
 const directLabel = formData.customHeaders?.[key] || layout.headers[key] || defaultHeadersMap[type]?.[key] || defaultHeadersMap.common?.[key];
 if (directLabel) return directLabel;
 const colMatch = String(key || "").match(/^col(\d+)(?:_group)?$/);
 if (master?.isSystem === false && colMatch) {
 const field = (master.fields || [])
 .filter((item) => item.defaultVisible !== false && !["srNo", "location", "areaNo", "uom", "remarks", "action"].includes(item.key))
 .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))[Number(colMatch[1]) - 1];
 if (field) return key.endsWith("_group") ? (field.groupLabel || field.label || field.key) : (field.label || field.key);
 }
 return defaultLabel;
 };

 const getMasterLayout = (master) => {
 const config = { ...(master?.defaultColumnConfig || {}) };
 const headers = { ...(master?.defaultHeaders || {}) };
 const settings = { ...(master?.defaultColumnSettings || {}) };

 if (master?.isSystem === false && Array.isArray(master?.fields) && master.fields.length > 0) {
 Object.keys(config).forEach((key) => delete config[key]);
 Object.keys(headers).forEach((key) => delete headers[key]);
 Object.keys(settings).forEach((key) => delete settings[key]);
 let customIndex = 1;
 master.fields
 .filter((field) => field.defaultVisible !== false)
 .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
 .forEach((field) => {
 const baseKey = ["srNo", "location", "areaNo", "uom", "remarks", "action"].includes(field.key) ? field.key : `col${customIndex++}`;
 config[baseKey] = true;
 headers[baseKey] = field.label || field.key || baseKey;
 settings[baseKey] = { ...(settings[baseKey] || {}), fieldType: field.inputType === "text" ? "text" : "number" };
 });
 }

 return { config, headers, settings };
 };

 const updateCustomHeader = (key, value) => {
 setFormData(prev => ({ ...prev, customHeaders: { ...prev.customHeaders, [key]: value } }));
 };

 const isColActive = useCallback((colKey) => {
 if (columnConfig[colKey] !== undefined) return columnConfig[colKey];
 const master = sheetTypeMasters.find((item) => item.code === formData.sheetType);
 const masterConfig = getMasterLayout(master).config;
 if (masterConfig && masterConfig[colKey] !== undefined) {
 return masterConfig[colKey];
 }
 const defaultActiveCols = [
 "srNo", "location", "uom", "from", "to", "wireSize", "circuitNo", "conduitSize", "remarks", "action",
 "pointHeader", "powerHeader", "subMainHeader", "primaryHeader", "secondaryHeader", "primaryPowerHeader", "secondaryPowerHeader",
 "fanFixingHeader", "lightFixingHeader", "conduitHeader", "elvConduitHeader", "cableRunHeader", "cableQtyHeader", "totalCablesHeader",
 "lengthHeader", "runHeader", "perforated1Header", "ladderHeader", "stripHeader", "perforated2Header",
 "tray300mm", "tray450mm", "tray600mm", "ladder900mm", "ladder1200mm", "strip25x3", "strip32x6", "tray150mm", "tray100mm",
 "description", "areaNo", "pointFrom", "pointTo", "pointQty", "powerFrom", "powerTo", "powerQty", "subMainFrom", "subMainTo", "subMainQty",
 "dbToSb", "dbTo1stLight", "sbTo1stPoint", "sbToSb", "pointToPoint"
 ];
 if (defaultActiveCols.includes(colKey)) return true;
 if (formData.sheetType === "SWITCH_SOCKET" && colKey.startsWith("col")) return parseInt(colKey.replace("col", "")) <= 10;
 if (formData.sheetType === "DB_INSTALLATION" && colKey.startsWith("col")) return parseInt(colKey.replace("col", "")) <= 8;
 if (formData.sheetType === "CONDUIT_SUMMARY" && ["col1", "col2", "col3", "col4", "col5", "col6"].includes(colKey)) return true;
 return false;
 }, [columnConfig, formData.sheetType, sheetTypeMasters]);

 const handleSheetTypeChange = (sheetType) => {
 const master = sheetTypeMasters.find((item) => item.code === sheetType);
 const layout = getMasterLayout(master);

 setFormData((prev) => ({
 ...prev,
 sheetType,
 customHeaders: layout.headers || prev.customHeaders || {},
 columnSettings: layout.settings || prev.columnSettings || {},
 }));

 setColumnConfig(layout.config || {});
 };

 const removeColumn = (colKey) => {
 setColumnConfig(prevConfig => {
 const newConfig = { ...prevConfig };
 setFormData(prevForm => {
 const newForm = { ...prevForm };
 const colSettings = newForm.columnSettings?.[colKey] || {};
 const colSpan = colSettings.colSpan || 1;
 if (colKey.startsWith("col")) {
 const num = parseInt(colKey.replace("col", ""));
 let parentKey = null;
 for (let i = 1; i < num; i++) {
 const pKey = `col${i}`;
 const pSpan = newForm.columnSettings?.[pKey]?.colSpan || 1;
 if (prevConfig[pKey] !== false && num < i + pSpan) { parentKey = pKey; break; }
 }
 if (parentKey) {
 newConfig[colKey] = false;
 const currentParentSpan = newForm.columnSettings[parentKey]?.colSpan || 1;
 newForm.columnSettings = { ...newForm.columnSettings, [parentKey]: { ...newForm.columnSettings[parentKey], colSpan: Math.max(1, currentParentSpan - 1) } };
 } else if (colSpan > 1) {
 for (let i = 0; i < colSpan; i++) { newConfig[`col${num + i}`] = false; }
 newConfig[colKey] = false;
 } else { newConfig[colKey] = false; }
 } else { newConfig[colKey] = false; }
 return newForm;
 });
 return newConfig;
 });
 };

 const fetchUsedQuantities = useCallback(async (woId) => {
 try {
 const excludeSheet = savedIdRef.current ? `?excludeSheet=${savedIdRef.current}` : "";
 const res = await api.get(`/measurement-sheets/used-qty/${woId}${excludeSheet}`);
 if (res.data.success) setUsedQtyMap(res.data.map);
 } catch (err) { console.error("Failed to fetch used quantities", err); }
 }, []);

 const loadBOQ = useCallback(async (woId) => {
 try {
 const res = await api.get(`/work-orders/${woId}/boq`);
 if (res.data.success) setBoqList(res.data.data.boqItems || res.data.data);
 } catch (err) { console.error(err); }
 }, []);

 // ==========================
 // AUTO-SAVE DRAFT LOGIC
 // ==========================
 
 // 1. Sync to LocalStorage (Immediate safety net for sidebar clicks)
 useEffect(() => {
 const hasData = formData.workOrder || formData.boqItem || rows.some(r => 
 (r.location && r.location.trim()) || 
 Object.entries(r).some(([key, val]) => typeof val === 'number' && val > 0 && !['srNo'].includes(key))
 );
 
 if (hasData) {
 const draftData = { formData, rows, columnConfig, savedId }; // Store savedId
 localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
 }
 }, [formData, rows, columnConfig, savedId]);

 // 2. Sync to DB (Debounced for server efficiency)
 const autoSaveToDB = useCallback(async () => {
 // PREVENT CONCURRENT SAVES
 if (isSavingRef.current) return;

 // Only auto-save if essential fields are selected
 if (!formData.project || !formData.workOrder || !formData.boqItem || !formData.sheetType) {
 return;
 }

 // STRICT CHECK: Only save to DB if substantial data is entered
 // (At least 1 row must have a location OR a non-zero numeric value)
 const hasSubstantialData = rows.some(r => 
 (r.location && r.location.trim().length > 2) || 
 Object.entries(r).some(([key, val]) => 
 typeof val === 'number' && val > 0 && !['srNo'].includes(key)
 )
 );

 if (!hasSubstantialData) return;

 try {
 const payload = {
 ...formData,
 boqItems: Array.isArray(formData.boqItem) ? formData.boqItem : [formData.boqItem],
 rows: rows,
 columnConfig,
 status: "Draft",
 };
 const saveSignature = JSON.stringify({ payload, targetId: savedIdRef.current || null });
 if (saveSignature === lastAutoSaveSignatureRef.current) return;

 isSavingRef.current = true;
 setIsAutoSaving(true);
 if (savedIdRef.current) {
 await api.put(`/measurement-sheets/${savedIdRef.current}`, payload, { suppressErrorToast: true });
 console.log("Auto-saved draft (updated)");
 } else {
 const res = await api.post("/measurement-sheets", payload, { suppressErrorToast: true });
 if (res.data.success && res.data.data?._id) {
 const newId = res.data.data._id;
 savedIdRef.current = newId;
 setSavedId(newId);
 console.log("Auto-saved draft (created)");
 }
 }
 lastAutoSaveSignatureRef.current = JSON.stringify({ payload, targetId: savedIdRef.current || null });
 } catch (err) {
 console.error("Auto-save to DB failed", err);
 } finally {
 setIsAutoSaving(false);
 isSavingRef.current = false;
 }
 }, [formData, rows, columnConfig]);

 useEffect(() => {
 const timer = setTimeout(() => {
 autoSaveToDB();
 }, 2000); // Debounce save for 2 seconds

 return () => clearTimeout(timer);
 }, [formData, rows, columnConfig, autoSaveToDB]);

 // Handle accidental closure/refresh
 useEffect(() => {
 const handleBeforeUnload = (e) => {
 // We can't reliably perform a final async save here in all browsers, 
 // but we can warn the user.
 const msg = "You have unsaved changes. Are you sure you want to leave?";
 e.returnValue = msg;
 return msg;
 };
 window.addEventListener("beforeunload", handleBeforeUnload);
 return () => window.removeEventListener("beforeunload", handleBeforeUnload);
 }, []);

 // Handle internal navigation (like Back button in UI)
 useEffect(() => {
 return () => {
 // Attempt a final save when the component unmounts
 // (This will fire when navigating away within the app)
 autoSaveToDB();
 };
 }, [autoSaveToDB]);

 useEffect(() => {
 // Standard localStorage fallback removed as we now save to DB
 // localStorage.removeItem(DRAFT_KEY); 
 }, []);

 useEffect(() => {
 const savedDraft = localStorage.getItem(DRAFT_KEY);
 if (savedDraft) {
 try {
 const { formData: dForm, rows: dRows, columnConfig: dCols, savedId: dId } = JSON.parse(savedDraft);
 const hasData = dForm.workOrder || dForm.boqItem || dRows.some(r => r.location || r.remarks || Object.values(r).some(v => typeof v === 'number' && v > 0));
 
 if (hasData) {
 swal({
 title: "Restore Draft?",
 text: "You have an unsaved draft. Would you like to restore it?",
 icon: "info",
 buttons: ["No, Start Fresh", "Yes, Restore"],
 }).then((willRestore) => {
 if (willRestore) {
 setFormData(dForm);
 setRows(dRows);
 setColumnConfig(dCols);
 lastAutoSaveSignatureRef.current = JSON.stringify({
 payload: {
 ...dForm,
 boqItems: Array.isArray(dForm.boqItem) ? dForm.boqItem : [dForm.boqItem],
 rows: dRows,
 columnConfig: dCols,
 status: "Draft",
 },
 targetId: dId || null,
 });
 if (dId) {
 setSavedId(dId); // Restore savedId
 savedIdRef.current = dId;
 }
 if (dForm.workOrder) {
 loadBOQ(dForm.workOrder);
 fetchUsedQuantities(dForm.workOrder);
 }
 } else {
 localStorage.removeItem(DRAFT_KEY);
 }
 });
 }
 } catch (e) {
 console.error("Failed to parse draft", e);
 }
 }
 }, [loadBOQ, fetchUsedQuantities]);

 const fetchInitialData = useCallback(async () => {
 try {
 const [woRes, uomRes, typeRes] = await Promise.allSettled([
 api.get("/work-orders?limit=1000"),
 api.get("/boq-unit-types"),
 api.get("/measurement-sheet-types"),
 ]);
 if (woRes.status === "fulfilled" && woRes.value.data.success) {
 setWorkOrders(woRes.value.data.data || []);
 }
 if (uomRes.status === "fulfilled" && uomRes.value.data.success) {
 setUomList(uomRes.value.data.data || []);
 }
 if (typeRes.status === "fulfilled" && typeRes.value.data.success) {
 setSheetTypeMasters(typeRes.value.data.data || fallbackSheetTypes);
 } else {
 setSheetTypeMasters(fallbackSheetTypes);
 }
 } catch (err) { console.error(err); }
 }, []);

 useEffect(() => {
 fetchInitialData();
 }, [fetchInitialData]);

 useEffect(() => {
 if (!sheetTypeMasters.length) return;
 if (Object.keys(columnConfig || {}).length > 0) return;

 const master = sheetTypeMasters.find((item) => item.code === formData.sheetType);
 if (!master) return;
 const layout = getMasterLayout(master);

 setColumnConfig(layout.config || {});
 setFormData((prev) => ({
 ...prev,
 customHeaders: Object.keys(prev.customHeaders || {}).length ? prev.customHeaders : (layout.headers || {}),
 columnSettings: Object.keys(prev.columnSettings || {}).length ? prev.columnSettings : (layout.settings || {}),
 }));
 }, [sheetTypeMasters, formData.sheetType, columnConfig]);

 useEffect(() => {
 if (workOrderFromUrl && workOrders.length > 0) {
 const wo = workOrders.find((w) => w._id === workOrderFromUrl);
 if (wo) {
 setFormData((prev) => ({ 
 ...prev, 
 workOrder: workOrderFromUrl, 
 project: wo.project?._id || wo.project || "", 
 location: wo.project?.location || wo.location || "", 
 area: wo.project?.area || wo.area || "" 
 }));
 loadBOQ(workOrderFromUrl);
 fetchUsedQuantities(workOrderFromUrl);
 }
 }
 }, [workOrders, workOrderFromUrl, loadBOQ, fetchUsedQuantities]);

 const handleWorkOrderChange = (opt) => {
 const wo = workOrders.find((w) => w._id === opt.value);
 setFormData((prev) => ({ ...prev, workOrder: opt.value, project: wo.project?._id || wo.project || "", location: wo.project?.location || wo.location || "", area: wo.project?.area || wo.area || "" }));
 loadBOQ(opt.value);
 fetchUsedQuantities(opt.value);
 };

 const handleBOQChange = (opts) => {
 const isMulti = Array.isArray(opts);
 const selectedValues = isMulti ? opts.map(o => o.value) : (opts ? [opts.value] : []);
 
 // For single select backward compatibility or reference logic
 // We use the first selected item to populate basic details like unit, wlino etc.
 const firstBoqId = selectedValues[0];
 const firstBoq = boqList.find((b) => b._id === firstBoqId);
 
 let totalApproved = 0;
 let totalUsed = 0;
 
 selectedValues.forEach(id => {
 const b = boqList.find(x => x._id === id);
 if (b) {
 totalApproved += Number(b.quantity || 0);
 totalUsed += Number(usedQtyMap[id] || 0);
 }
 });
 
 setFormData((prev) => ({ 
 ...prev, 
 boqItem: selectedValues, 
 itemName: firstBoq?.itemName || (selectedValues.length > 1 ? `${selectedValues.length} items selected` : ""), 
 description: firstBoq?.description || "", 
 unit: firstBoq?.unit || "", 
 approvedQty: totalApproved, 
 usedQtyBefore: totalUsed, 
 remainingQtyBefore: totalApproved - totalUsed, 
 wlino: firstBoq?.wlino || "", 
 hsnSac: firstBoq?.hsnSac || "" 
 }));
 if (firstBoqId) {
 setRows(prevRows => clampAllRowsToBoqCaps(prevRows.map((r) => ({
 ...r,
 uom: firstBoq?.unit || r.uom,
 boqItemId: selectedValues.includes(r.boqItemId) ? r.boqItemId : firstBoqId,
 })), selectedValues));
 }
 };

 const numericRowFields = new Set([
 "pointQty", "powerQty", "subMainQty",
 "primaryPointDbToSb", "primaryPointDbTo1stLight", "primaryPointSbTo1stPoint",
 "secondaryPointSbToSb", "secondaryPointPointToPoint",
 "primaryPowerPoints", "secondaryPowerPoints",
 "conduit20mm", "conduit25mm", "conduit32mm",
 "elvConduit20mm", "elvConduit25mm", "elvConduit32mm",
 "cableRun", "cableQty", "totalCables",
 "length", "run",
 "tray300mm", "tray450mm", "tray600mm",
 "ladder900mm", "ladder1200mm",
 "strip25x3", "strip32x6",
 "tray150mm", "tray100mm",
 "fanFixing", "lightFixing",
 ]);
 for (let i = 1; i <= 20; i++) numericRowFields.add(`col${i}`);

 const toNonNegativeNumber = (value) => {
 const numberValue = Number(value);
 if (!Number.isFinite(numberValue)) return 0;
 return Math.max(0, numberValue);
 };

 const getRemainingQty = (data = formData) => Math.max(0, Number(data.approvedQty || 0) - Number(data.usedQtyBefore || 0));

 const getBoqRemainingQty = (boqId) => {
 const boq = boqList.find((item) => item._id === boqId);
 if (!boq) return getRemainingQty();
 return Math.max(0, Number(boq.quantity || 0) - Number(usedQtyMap[boqId] || 0));
 };

 const getRowBoqId = (row) => row?.boqItemId || formData.boqItem?.[0] || "";

 const isNumericRowField = (field) => {
 if (/^col\d+$/.test(field)) {
 return (formData.columnSettings?.[field]?.fieldType || "number") === "number";
 }
 return numericRowFields.has(field);
 };

 const getCalculatedQtyFromTotals = (nextTotals, sheetType = formData.sheetType) => {
 if (sheetType === "ELECTRICAL_LIGHTING") return nextTotals.pointQty + nextTotals.powerQty + nextTotals.subMainQty;
 if (sheetType === "CONDUIT_MEASUREMENT") return nextTotals.primaryPointDbToSb + nextTotals.primaryPointDbTo1stLight + nextTotals.primaryPointSbTo1stPoint + nextTotals.secondaryPointSbToSb + nextTotals.secondaryPointPointToPoint + nextTotals.primaryPowerPoints + nextTotals.secondaryPowerPoints;
 if (sheetType === "CONDUIT_SUMMARY") return nextTotals.conduit20mm + nextTotals.conduit25mm + nextTotals.conduit32mm + nextTotals.elvConduit20mm + nextTotals.elvConduit25mm + nextTotals.elvConduit32mm;
 if (sheetType === "SWITCH_SOCKET" || sheetType === "DB_INSTALLATION") {
 let total = 0;
 for (let i = 1; i <= 20; i++) {
 if (isColActive(`col${i}`)) total += nextTotals[`col${i}`];
 }
 return total;
 }
 if (sheetType === "CABLE_MEASUREMENT") return nextTotals.totalCables;
 if (sheetType === "CABLE_TRAY_SUMMARY") return nextTotals.tray300mm + nextTotals.tray450mm + nextTotals.tray600mm + nextTotals.ladder900mm + nextTotals.ladder1200mm + nextTotals.strip25x3 + nextTotals.strip32x6 + nextTotals.tray150mm + nextTotals.tray100mm;
 if (sheetType === "WIRING_MEASUREMENT") return nextTotals.primaryPointDbToSb + nextTotals.primaryPointDbTo1stLight + nextTotals.primaryPointSbTo1stPoint + nextTotals.secondaryPointSbToSb + nextTotals.secondaryPointPointToPoint + nextTotals.primaryPowerPoints + nextTotals.secondaryPowerPoints + nextTotals.fanFixing + nextTotals.lightFixing;
 let customTotal = 0;
 for (let i = 1; i <= 20; i++) {
 const colKey = `col${i}`;
 if (isColActive(colKey) && (formData.columnSettings?.[colKey]?.fieldType || "number") === "number") {
 customTotal += Number(nextTotals[colKey] || 0);
 }
 }
 return customTotal;
 };

 const getRowCalculatedQty = (row) => getCalculatedQtyFromTotals(calculateTotals([row]));

 const hasQuantityFields = () => {
 const quantitySheetTypes = [
 "ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CONDUIT_SUMMARY",
 "SWITCH_SOCKET", "DB_INSTALLATION", "CABLE_MEASUREMENT",
 "CABLE_TRAY_SUMMARY", "WIRING_MEASUREMENT",
 ];
 if (quantitySheetTypes.includes(formData.sheetType)) return true;
 for (let i = 1; i <= 20; i++) {
 const colKey = `col${i}`;
 if (isColActive(colKey) && (formData.columnSettings?.[colKey]?.fieldType || "number") === "number") return true;
 }
 return false;
 };

 const rowHasData = (row) => Object.entries(row).some(([key, value]) => {
 if (["srNo", "boqItemId", "uom"].includes(key)) return false;
 if (typeof value === "number") return value > 0;
 if (typeof value === "string") return value.trim() !== "";
 return Boolean(value);
 });

 const getRowsQtyForBoq = (nextRows, boqId) => {
 return nextRows.reduce((total, row) => {
 if (getRowBoqId(row) !== boqId) return total;
 return total + getRowCalculatedQty(row);
 }, 0);
 };

 const clampRowsToRemainingQty = (nextRows, index, field, desiredValue) => {
 const boqId = getRowBoqId(nextRows[index]);
 const remainingQty = boqId ? getBoqRemainingQty(boqId) : getRemainingQty();
 if (!isNumericRowField(field) || remainingQty <= 0) {
 nextRows[index][field] = 0;
 return nextRows;
 }
 const nextCalculatedQty = boqId ? getRowsQtyForBoq(nextRows, boqId) : getCalculatedQtyFromTotals(calculateTotals(nextRows));
 if (nextCalculatedQty <= remainingQty) return nextRows;

 let low = 0;
 let high = desiredValue;
 let best = 0;
 for (let i = 0; i < 24; i++) {
 const mid = (low + high) / 2;
 const testRows = nextRows.map((row, rowIndex) => {
 if (rowIndex !== index) return row;
 const updatedRow = { ...row, [field]: mid };
 if (formData.sheetType === "CABLE_MEASUREMENT" && (field === "cableRun" || field === "cableQty")) {
 updatedRow.totalCables = Number((Number(updatedRow.cableRun || 0) * Number(updatedRow.cableQty || 0)).toFixed(2));
 }
 return updatedRow;
 });
 const testQty = boqId ? getRowsQtyForBoq(testRows, boqId) : getCalculatedQtyFromTotals(calculateTotals(testRows));
 if (testQty <= remainingQty) {
 best = mid;
 low = mid;
 } else {
 high = mid;
 }
 }
 nextRows[index][field] = Number(best.toFixed(2));
 if (formData.sheetType === "CABLE_MEASUREMENT" && (field === "cableRun" || field === "cableQty")) {
 nextRows[index].totalCables = Number((Number(nextRows[index].cableRun || 0) * Number(nextRows[index].cableQty || 0)).toFixed(2));
 }
 return nextRows;
 };

 const clearNumericRowValues = (row) => {
 const nextRow = { ...row };
 numericRowFields.forEach((field) => {
 if (isNumericRowField(field)) nextRow[field] = 0;
 });
 return nextRow;
 };

 function clampAllRowsToBoqCaps(sourceRows, selectedBoqIds = formData.boqItem || []) {
 const usedByBoq = {};
 return sourceRows.map((row) => {
 const fallbackBoqId = selectedBoqIds[0] || "";
 const boqId = selectedBoqIds.includes(row.boqItemId) ? row.boqItemId : fallbackBoqId;
 let nextRow = { ...row, boqItemId: boqId };
 const remainingQty = boqId ? getBoqRemainingQty(boqId) : getRemainingQty();
 if (remainingQty <= 0) return clearNumericRowValues(nextRow);

 const allowedQty = Math.max(0, remainingQty - Number(usedByBoq[boqId] || 0));
 const rowQty = getRowCalculatedQty(nextRow);
 if (rowQty > allowedQty) {
 nextRow = clearNumericRowValues(nextRow);
 } else {
 usedByBoq[boqId] = Number(usedByBoq[boqId] || 0) + rowQty;
 }
 return nextRow;
 });
 }

 const handleRowChange = (index, field, value) => {
 setRows(prevRows => {
 const newRows = [...prevRows];
 if (field === "boqItemId") {
 const currentRow = { ...newRows[index], boqItemId: value };
 newRows[index] = getBoqRemainingQty(value) <= 0 ? clearNumericRowValues(currentRow) : currentRow;
 return newRows;
 }
 const nextValue = isNumericRowField(field) ? toNonNegativeNumber(value) : value;
 newRows[index] = { ...newRows[index], [field]: nextValue };
 if (formData.sheetType === "CABLE_MEASUREMENT" && (field === "cableRun" || field === "cableQty")) {
 newRows[index].totalCables = Number((Number(newRows[index].cableRun || 0) * Number(newRows[index].cableQty || 0)).toFixed(2));
 }
 if (isNumericRowField(field)) return clampRowsToRemainingQty(newRows, index, field, nextValue);
 return newRows;
 });
 };

 const addRow = () => {
 setRows(prevRows => [...prevRows, { srNo: prevRows.length + 1, boqItemId: formData.boqItem?.[0] || "", location: "", uom: formData.unit || "NOS", wireSize: "", circuitNumber: "", conduitSize: "", pointFrom: "", pointTo: "", pointQty: 0, powerFrom: "", powerTo: "", powerQty: 0, subMainFrom: "", subMainTo: "", subMainQty: 0, primaryPointDbToSb: 0, primaryPointDbTo1stLight: 0, primaryPointSbTo1stPoint: 0, secondaryPointSbToSb: 0, secondaryPointPointToPoint: 0, primaryPowerPoints: 0, secondaryPowerPoints: 0, col1: 0, col2: 0, col3: 0, col4: 0, col5: 0, col6: 0, col7: 0, col8: 0, col9: 0, col10: 0, col11: 0, col12: 0, col13: 0, col14: 0, col15: 0, col16: 0, col17: 0, col18: 0, col19: 0, col20: 0, conduit20mm: 0, conduit25mm: 0, conduit32mm: 0, elvConduit20mm: 0, elvConduit25mm: 0, elvConduit32mm: 0, length: 0, run: 0, tray300mm: 0, tray450mm: 0, tray600mm: 0, ladder900mm: 0, ladder1200mm: 0, strip25x3: 0, strip32x6: 0, tray150mm: 0, tray100mm: 0, fanFixing: 0, lightFixing: 0, cableFrom: "", cableTo: "", remarks: "" }]);
 };

 const removeRow = (index) => { 
 setRows(prevRows => {
 if (prevRows.length > 1) {
 return prevRows.filter((_, i) => i !== index).map((r, i) => ({ ...r, srNo: i + 1 }));
 }
 return prevRows;
 });
 };

 const calculateTotals = (sourceRows = rows) => {
 return sourceRows.reduce((acc, row) => {
 acc.pointQty += Number(row.pointQty || 0); acc.powerQty += Number(row.powerQty || 0); acc.subMainQty += Number(row.subMainQty || 0);
 acc.primaryPointDbToSb += Number(row.primaryPointDbToSb || 0); acc.primaryPointDbTo1stLight += Number(row.primaryPointDbTo1stLight || 0); acc.primaryPointSbTo1stPoint += Number(row.primaryPointSbTo1stPoint || 0);
 acc.secondaryPointSbToSb += Number(row.secondaryPointSbToSb || 0); acc.secondaryPointPointToPoint += Number(row.secondaryPointPointToPoint || 0);
 acc.primaryPowerPoints += Number(row.primaryPowerPoints || 0); acc.secondaryPowerPoints += Number(row.secondaryPowerPoints || 0);
 acc.conduit20mm += Number(row.col1 || row.conduit20mm || 0); acc.conduit25mm += Number(row.col2 || row.conduit25mm || 0); acc.conduit32mm += Number(row.col3 || row.conduit32mm || 0);
 acc.elvConduit20mm += Number(row.col4 || row.elvConduit20mm || 0); acc.elvConduit25mm += Number(row.col5 || row.elvConduit25mm || 0); acc.elvConduit32mm += Number(row.col6 || row.elvConduit32mm || 0);
 acc.cableRun += Number(row.cableRun || 0); acc.cableQty += Number(row.cableQty || 0); acc.totalCables += Number(row.totalCables || 0);
 acc.tray300mm += Number(row.tray300mm || 0); acc.tray450mm += Number(row.tray450mm || 0); acc.tray600mm += Number(row.tray600mm || 0);
 acc.ladder900mm += Number(row.ladder900mm || 0); acc.ladder1200mm += Number(row.ladder1200mm || 0); acc.strip25x3 += Number(row.strip25x3 || 0); acc.strip32x6 += Number(row.strip32x6 || 0);
 acc.tray150mm += Number(row.tray150mm || 0); acc.tray100mm += Number(row.tray100mm || 0); acc.fanFixing += Number(row.fanFixing || 0); acc.lightFixing += Number(row.lightFixing || 0);
 for (let i = 1; i <= 20; i++) {
 const val = Number(row[`col${i}`] || 0);
 if (!Number.isNaN(val)) acc[`col${i}`] += val;
 }
 return acc;
 }, { pointQty: 0, powerQty: 0, subMainQty: 0, primaryPointDbToSb: 0, primaryPointDbTo1stLight: 0, primaryPointSbTo1stPoint: 0, secondaryPointSbToSb: 0, secondaryPointPointToPoint: 0, primaryPowerPoints: 0, secondaryPowerPoints: 0, conduit20mm: 0, conduit25mm: 0, conduit32mm: 0, elvConduit20mm: 0, elvConduit25mm: 0, elvConduit32mm: 0, cableRun: 0, cableQty: 0, totalCables: 0, tray300mm: 0, tray450mm: 0, tray600mm: 0, ladder900mm: 0, ladder1200mm: 0, strip25x3: 0, strip32x6: 0, tray150mm: 0, tray100mm: 0, fanFixing: 0, lightFixing: 0, col1: 0, col2: 0, col3: 0, col4: 0, col5: 0, col6: 0, col7: 0, col8: 0, col9: 0, col10: 0, col11: 0, col12: 0, col13: 0, col14: 0, col15: 0, col16: 0, col17: 0, col18: 0, col19: 0, col20: 0 });
 };

 const openGroupModal = () => { setModalData({ parent: "", subs: [""], fieldType: "number" }); setShowModal(true); };

 const handleModalSave = () => {
 if (!modalData.parent.trim()) return swal("Error", "Heading name is required", "error");
 const validSubs = modalData.subs.filter(s => s.trim());
 const subCount = validSubs.length || 1;
 
 // Find available columns that are NOT currently active
 let startNum = -1;
 for (let i = 1; i <= 20; i++) {
 let available = true;
 for (let j = 0; j < subCount; j++) {
 const colKey = `col${i + j}`;
 if (columnConfig[colKey] === true || (i + j) > 20) { 
 available = false; 
 break; 
 }
 }
 if (available) { startNum = i; break; }
 }

 if (startNum === -1) return swal("Error", "Not enough available columns to add this group", "error");

 const parentKey = `col${startNum}`;
 const newCustomHeaders = { ...formData.customHeaders };
 const newColumnSettings = { ...(formData.columnSettings || {}) };
 const updates = {};

 if (validSubs.length > 0) {
 // CASE 1: Heading with Sub-headings
 newCustomHeaders[`${parentKey}_group`] = modalData.parent;
 newColumnSettings[parentKey] = { 
 ...newColumnSettings[parentKey], 
 colSpan: subCount,
 fieldType: modalData.fieldType,
 isGroupParent: true 
 };

 validSubs.forEach((subName, idx) => { 
 const colKey = `col${startNum + idx}`; 
 updates[colKey] = true; 
 newCustomHeaders[colKey] = subName; 
 newColumnSettings[colKey] = { ...newColumnSettings[colKey], fieldType: modalData.fieldType };
 });
 } else {
 // CASE 2: Heading with NO Sub-headings (Single Column)
 const colKey = parentKey;
 updates[colKey] = true;
 newCustomHeaders[colKey] = modalData.parent;
 newColumnSettings[colKey] = { 
 ...newColumnSettings[colKey], 
 colSpan: 1, 
 fieldType: modalData.fieldType,
 isGroupParent: false
 };
 }

 setFormData(prev => ({ 
 ...prev, 
 customHeaders: newCustomHeaders, 
 columnSettings: newColumnSettings 
 }));
 
 setColumnConfig(prev => ({ ...prev, ...updates }));
 setShowModal(false);
 setModalData({ parent: "", subs: [""], fieldType: "number" });
 };

 const totals = calculateTotals();

 useEffect(() => {
 const { sheetType } = formData;
 const q = getCalculatedQtyFromTotals(totals, sheetType);
 
 setFormData(prev => {
 const newCalculated = Math.max(0, Number(q.toFixed(2)));
 const remainingQty = getRemainingQty(prev);
 // Only auto-update installedQty if it's 0 or matches previous calculated qty exactly
 const shouldUpdateInstalled = prev.installedQty === 0 || prev.installedQty === prev.calculatedQty;
 const nextInstalledQty = shouldUpdateInstalled ? newCalculated : toNonNegativeNumber(prev.installedQty);
 const nextInstalled = Math.min(nextInstalledQty, remainingQty);
 if (Number(prev.calculatedQty || 0) === newCalculated && Number(prev.installedQty || 0) === nextInstalled) return prev;
 return { ...prev, calculatedQty: newCalculated, installedQty: nextInstalled };
 });
 }, [totals, formData.sheetType, isColActive]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!formData.workOrder || !formData.boqItem) return swal("Error", "Please select Work Order and BOQ Item", "error");
 
 const calculatedQty = toNonNegativeNumber(getCalculatedQtyFromTotals(calculateTotals(), formData.sheetType));
 const installedQty = toNonNegativeNumber(formData.installedQty || formData.calculatedQty || calculatedQty);
 const remainingQty = getRemainingQty();
 // if (hasQuantityFields() && installedQty <= 0) return swal("Error", "Installed quantity must be greater than 0", "error");
 if (installedQty > remainingQty) {
 return swal("Error", `Installed quantity (${installedQty}) exceeds remaining BOQ quantity (${remainingQty.toFixed(2)})`, "error");
 }

 const validRows = rows.filter(rowHasData);
 if (validRows.length === 0) return swal("Error", "Please add at least one row with data", "error");
 
 setLoading(true);
 try {
 const data = { ...formData, installedQty, rows: validRows, totals: calculateTotals(), columnConfig, status: "Submitted" };
 let res;
 if (savedIdRef.current) {
 res = await api.put(`/measurement-sheets/${savedIdRef.current}`, data);
 } else {
 res = await api.post("/measurement-sheets", data);
 }

 if (res.data.success) { 
 localStorage.removeItem(DRAFT_KEY);
 swal("Success", "Measurement Sheet submitted successfully", "success"); 
 navigate(-1); 
 }
 } catch (err) { swal("Error", err.response?.data?.message || err.message, "error"); } finally { setLoading(false); }
 };

 const renderRow1 = () => {
 const { sheetType } = formData;
 const cells = [];
 if (formData.boqItem && formData.boqItem.length > 1) {
 cells.push(<EditableHeader key="boqRow" label="BOQ ITEM" keyName="boqRow" rowSpan={2} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("srNo")) cells.push(<EditableHeader key="sr" label={getHeaderLabel(sheetType, "srNo", "SR.NO")} keyName="srNo" rowSpan={2} onRemove={() => removeColumn("srNo")} updateCustomHeader={updateCustomHeader} />);
 const locLabel = sheetType === "SWITCH_SOCKET" || sheetType === "DB_INSTALLATION" ? "DESCRIPTION / LOCATION" : "LOCATION";
 if (isColActive("location")) cells.push(<EditableHeader key="loc" label={getHeaderLabel(sheetType, "location", locLabel)} keyName="location" rowSpan={2} style={{ minWidth: "200px" }} onRemove={() => removeColumn("location")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("areaNo")) cells.push(<EditableHeader key="areaN" label={getHeaderLabel(sheetType, "areaNo", "AREA NO.")} keyName="areaNo" rowSpan={2} onRemove={() => removeColumn("areaNo")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("uom")) cells.push(<EditableHeader key="uom" label={getHeaderLabel(sheetType, "uom", "UOM")} keyName="uom" rowSpan={2} onRemove={() => removeColumn("uom")} updateCustomHeader={updateCustomHeader} />);
 
 if (sheetType === "CONDUIT_SUMMARY" || sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("from")) cells.push(<EditableHeader key="from" label={getHeaderLabel(sheetType, "from", "FROM")} keyName="from" rowSpan={2} onRemove={() => removeColumn("from")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("to")) cells.push(<EditableHeader key="to" label={getHeaderLabel(sheetType, "to", "TO")} keyName="to" rowSpan={2} onRemove={() => removeColumn("to")} updateCustomHeader={updateCustomHeader} />);
 }
 
 if (["ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType)) {
 if (!["CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType) && isColActive("wireSize")) cells.push(<EditableHeader key="wire" label={getHeaderLabel(sheetType, "wireSize", "WIRE SIZES")} keyName="wireSize" rowSpan={2} style={{ minWidth: "120px" }} onRemove={() => removeColumn("wireSize")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("circuitNo")) cells.push(<EditableHeader key="circ" label={getHeaderLabel(sheetType, "circuitNo", "CIRCUIT NO.")} keyName="circuitNo" rowSpan={2} style={{ minWidth: "120px" }} onRemove={() => removeColumn("circuitNo")} updateCustomHeader={updateCustomHeader} />);
 }
 
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("conduitSize")) cells.push(<EditableHeader key="conduit" label={getHeaderLabel(sheetType, "conduitSize", "CONDUIT SIZE")} keyName="conduitSize" rowSpan={2} style={{ minWidth: "120px" }} onRemove={() => removeColumn("conduitSize")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("pointHeader")) cells.push(<EditableHeader key="pHead" label={getHeaderLabel(sheetType, "pointHeader", "LIGHT / FAN / EXHAUST POINT")} keyName="pointHeader" colSpan={3} onRemove={() => removeColumn("pointHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("powerHeader")) cells.push(<EditableHeader key="powHead" label={getHeaderLabel(sheetType, "powerHeader", "POWER POINTS")} keyName="powerHeader" colSpan={3} onRemove={() => removeColumn("powerHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("subMainHeader")) cells.push(<EditableHeader key="subHead" label={getHeaderLabel(sheetType, "subMainHeader", "SUB MAINS")} keyName="subMainHeader" colSpan={3} onRemove={() => removeColumn("subMainHeader")} updateCustomHeader={updateCustomHeader} />);
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) cells.push(<EditableHeader key="pri" label={getHeaderLabel(sheetType, "primaryHeader", "PRIMARY POINT")} keyName="primaryHeader" colSpan={3} onRemove={() => removeColumn("primaryHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("secondaryHeader")) cells.push(<EditableHeader key="sec" label={getHeaderLabel(sheetType, "secondaryHeader", "SECONDARY POINTS")} keyName="secondaryHeader" colSpan={2} onRemove={() => removeColumn("secondaryHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("primaryPowerHeader")) cells.push(<EditableHeader key="priP" label={getHeaderLabel(sheetType, "primaryPowerHeader", "PRI POWER")} keyName="primaryPowerHeader" rowSpan={2} onRemove={() => removeColumn("primaryPowerHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("secondaryPowerHeader")) cells.push(<EditableHeader key="secP" label={getHeaderLabel(sheetType, "secondaryPowerHeader", "SEC POWER")} keyName="secondaryPowerHeader" rowSpan={2} onRemove={() => removeColumn("secondaryPowerHeader")} updateCustomHeader={updateCustomHeader} />);
 if (sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("fanFixingHeader")) cells.push(<EditableHeader key="fan" label={getHeaderLabel(sheetType, "fanFixingHeader", "FAN FIXING")} keyName="fanFixingHeader" rowSpan={2} onRemove={() => removeColumn("fanFixingHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("lightFixingHeader")) cells.push(<EditableHeader key="light" label={getHeaderLabel(sheetType, "lightFixingHeader", "LIGHT FIXING")} keyName="lightFixingHeader" rowSpan={2} onRemove={() => removeColumn("lightFixingHeader")} updateCustomHeader={updateCustomHeader} />);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) cells.push(<EditableHeader key="conH" label={getHeaderLabel(sheetType, "conduitHeader", "CONDUIT")} keyName="conduitHeader" colSpan={3} onRemove={() => removeColumn("conduitHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("elvConduitHeader")) cells.push(<EditableHeader key="elvH" label={getHeaderLabel(sheetType, "elvConduitHeader", "ELV CONDUIT")} keyName="elvConduitHeader" colSpan={3} onRemove={() => removeColumn("elvConduitHeader")} updateCustomHeader={updateCustomHeader} />);
 } else if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("cableRunHeader")) cells.push(<EditableHeader key="cRun" label={getHeaderLabel(sheetType, "cableRunHeader", "CABLE RUN")} keyName="cableRunHeader" rowSpan={2} onRemove={() => removeColumn("cableRunHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("cableQtyHeader")) cells.push(<EditableHeader key="cQty" label={getHeaderLabel(sheetType, "cableQtyHeader", "CABLE QTY.")} keyName="cableQtyHeader" rowSpan={2} onRemove={() => removeColumn("cableQtyHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("totalCablesHeader")) cells.push(<EditableHeader key="cTotal" label={getHeaderLabel(sheetType, "totalCablesHeader", "TOTAL CABLES")} keyName="totalCablesHeader" rowSpan={2} onRemove={() => removeColumn("totalCablesHeader")} updateCustomHeader={updateCustomHeader} />);
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("lengthHeader")) cells.push(<EditableHeader key="len" label={getHeaderLabel(sheetType, "lengthHeader", "LENGTH")} keyName="lengthHeader" rowSpan={2} onRemove={() => removeColumn("lengthHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("runHeader")) cells.push(<EditableHeader key="run" label={getHeaderLabel(sheetType, "runHeader", "RUN")} keyName="runHeader" rowSpan={2} onRemove={() => removeColumn("runHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("perforated1Header")) cells.push(<EditableHeader key="perf1" label={getHeaderLabel(sheetType, "perforated1Header", "PERFORATED")} keyName="perforated1Header" colSpan={3} onRemove={() => removeColumn("perforated1Header")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("ladderHeader")) cells.push(<EditableHeader key="lad" label={getHeaderLabel(sheetType, "ladderHeader", "LADDER TRAY")} keyName="ladderHeader" colSpan={2} onRemove={() => removeColumn("ladderHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("stripHeader")) cells.push(<EditableHeader key="strip" label={getHeaderLabel(sheetType, "stripHeader", "G.I STRIP")} keyName="stripHeader" colSpan={2} onRemove={() => removeColumn("stripHeader")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("perforated2Header")) cells.push(<EditableHeader key="perf2" label={getHeaderLabel(sheetType, "perforated2Header", "PERFORATED")} keyName="perforated2Header" colSpan={2} onRemove={() => removeColumn("perforated2Header")} updateCustomHeader={updateCustomHeader} />);
 }
 for (let num = 1; num <= 20; num++) {
 const colKey = `col${num}`;
 if (!isColActive(colKey)) continue;

 // Check if this column is a child of some parent group
 let parentKey = null;
 for (let i = 1; i < num; i++) {
 const pKey = `col${i}`;
 const pSpan = formData.columnSettings?.[pKey]?.colSpan || 1;
 if (isColActive(pKey) && num < i + pSpan) {
 parentKey = pKey;
 break;
 }
 }

 if (parentKey) continue; // Skip children in Row 1

 const colSettings = formData.columnSettings?.[colKey] || {};
 const colSpan = colSettings.colSpan || 1;
 const isGroupParent = colSettings.isGroupParent || colSpan > 1;
 const labelKey = isGroupParent ? `${colKey}_group` : colKey;
 const defaultLabel = `Col ${num}`;

 cells.push(
 <EditableHeader 
 key={colKey} 
 label={getHeaderLabel(sheetType, labelKey, defaultLabel)} 
 keyName={labelKey} 
 rowSpan={isGroupParent ? 1 : 2} 
 colSpan={colSpan} 
 onRemove={() => removeColumn(colKey)} 
 updateCustomHeader={updateCustomHeader} 
 />
 );
 }
 if (isColActive("remarks")) cells.push(<EditableHeader key="rem" label={getHeaderLabel(sheetType, "remarks", "REMARKS")} keyName="remarks" rowSpan={2} style={{ minWidth: "150px" }} onRemove={() => removeColumn("remarks")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("action")) cells.push(<EditableHeader key="act" label={getHeaderLabel(sheetType, "action", "ACTION")} keyName="action" rowSpan={2} onRemove={() => removeColumn("action")} updateCustomHeader={updateCustomHeader} />);
 return cells;
 };

 const renderRow2 = () => {
 const { sheetType } = formData;
 const cells = [];
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("pointHeader")) {
 if (isColActive("pointFrom")) cells.push(<EditableHeader key="pf" label={getHeaderLabel(sheetType, "pointFrom", "FROM")} keyName="pointFrom" onRemove={() => removeColumn("pointFrom")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("pointTo")) cells.push(<EditableHeader key="pt" label={getHeaderLabel(sheetType, "pointTo", "TO")} keyName="pointTo" onRemove={() => removeColumn("pointTo")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("pointQty")) cells.push(<EditableHeader key="pq" label={getHeaderLabel(sheetType, "pointQty", "QTY")} keyName="pointQty" onRemove={() => removeColumn("pointQty")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("powerHeader")) {
 if (isColActive("powerFrom")) cells.push(<EditableHeader key="pwf" label={getHeaderLabel(sheetType, "powerFrom", "FROM")} keyName="powerFrom" onRemove={() => removeColumn("powerFrom")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("powerTo")) cells.push(<EditableHeader key="pwt" label={getHeaderLabel(sheetType, "powerTo", "TO")} keyName="powerTo" onRemove={() => removeColumn("powerTo")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("powerQty")) cells.push(<EditableHeader key="pwq" label={getHeaderLabel(sheetType, "powerQty", "QTY")} keyName="powerQty" onRemove={() => removeColumn("powerQty")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("subMainHeader")) {
 if (isColActive("subMainFrom")) cells.push(<EditableHeader key="smf" label={getHeaderLabel(sheetType, "subMainFrom", "FROM")} keyName="subMainFrom" onRemove={() => removeColumn("subMainFrom")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("subMainTo")) cells.push(<EditableHeader key="smt" label={getHeaderLabel(sheetType, "subMainTo", "TO")} keyName="subMainTo" onRemove={() => removeColumn("subMainTo")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("subMainQty")) cells.push(<EditableHeader key="smq" label={getHeaderLabel(sheetType, "subMainQty", "QTY")} keyName="subMainQty" onRemove={() => removeColumn("subMainQty")} updateCustomHeader={updateCustomHeader} />);
 }
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) {
 if (isColActive("dbToSb")) cells.push(<EditableHeader key="dbsb" label={getHeaderLabel(sheetType, "dbToSb", "DB-SB")} keyName="dbToSb" onRemove={() => removeColumn("dbToSb")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("dbTo1stLight")) cells.push(<EditableHeader key="db1l" label={getHeaderLabel(sheetType, "dbTo1stLight", "DB-1L")} keyName="dbTo1stLight" onRemove={() => removeColumn("dbTo1stLight")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("sbTo1stPoint")) cells.push(<EditableHeader key="sb1p" label={getHeaderLabel(sheetType, "sbTo1stPoint", "SB-1P")} keyName="sbTo1stPoint" onRemove={() => removeColumn("sbTo1stPoint")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("secondaryHeader")) {
 if (isColActive("sbToSb")) cells.push(<EditableHeader key="sbsb" label={getHeaderLabel(sheetType, "sbToSb", "SB-SB")} keyName="sbsb" onRemove={() => removeColumn("sbsb")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("pointToPoint")) cells.push(<EditableHeader key="ptp" label={getHeaderLabel(sheetType, "pointToPoint", "P-P")} keyName="pointToPoint" onRemove={() => removeColumn("pointToPoint")} updateCustomHeader={updateCustomHeader} />);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) {
 if (isColActive("col1")) cells.push(<EditableHeader key="c20" label={getHeaderLabel(sheetType, "col1", "20mm")} keyName="col1" onRemove={() => removeColumn("col1")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("col2")) cells.push(<EditableHeader key="c25" label={getHeaderLabel(sheetType, "col2", "25mm")} keyName="col2" onRemove={() => removeColumn("col2")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("col3")) cells.push(<EditableHeader key="c32" label={getHeaderLabel(sheetType, "col3", "32mm")} keyName="col3" onRemove={() => removeColumn("col3")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("elvConduitHeader")) {
 if (isColActive("col4")) cells.push(<EditableHeader key="col4" label={getHeaderLabel(sheetType, "col4", "20mm")} keyName="col4" onRemove={() => removeColumn("col4")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("col5")) cells.push(<EditableHeader key="col5" label={getHeaderLabel(sheetType, "col5", "25mm")} keyName="col5" onRemove={() => removeColumn("col5")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("col6")) cells.push(<EditableHeader key="col6" label={getHeaderLabel(sheetType, "col6", "32mm")} keyName="col6" onRemove={() => removeColumn("col6")} updateCustomHeader={updateCustomHeader} />);
 }
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("perforated1Header")) {
 if (isColActive("tray300mm")) cells.push(<EditableHeader key="t300" label={getHeaderLabel(sheetType, "tray300mm", "300mm")} keyName="tray300mm" onRemove={() => removeColumn("tray300mm")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("tray450mm")) cells.push(<EditableHeader key="t450" label={getHeaderLabel(sheetType, "tray450mm", "450mm")} keyName="tray450mm" onRemove={() => removeColumn("tray450mm")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("tray600mm")) cells.push(<EditableHeader key="t600" label={getHeaderLabel(sheetType, "tray600mm", "600mm")} keyName="tray600mm" onRemove={() => removeColumn("tray600mm")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("ladderHeader")) {
 if (isColActive("ladder900mm")) cells.push(<EditableHeader key="l900" label={getHeaderLabel(sheetType, "ladder900mm", "900mm")} keyName="ladder900mm" onRemove={() => removeColumn("ladder900mm")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("ladder1200mm")) cells.push(<EditableHeader key="l1200" label={getHeaderLabel(sheetType, "ladder1200mm", "1200mm")} keyName="ladder1200mm" onRemove={() => removeColumn("ladder1200mm")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("stripHeader")) {
 if (isColActive("strip25x3")) cells.push(<EditableHeader key="s25" label={getHeaderLabel(sheetType, "strip25x3", "25x3")} keyName="strip25x3" onRemove={() => removeColumn("strip25x3")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("strip32x6")) cells.push(<EditableHeader key="s32" label={getHeaderLabel(sheetType, "strip32x6", "32x6")} keyName="strip32x6" onRemove={() => removeColumn("strip32x6")} updateCustomHeader={updateCustomHeader} />);
 }
 if (isColActive("perforated2Header")) {
 if (isColActive("tray150mm")) cells.push(<EditableHeader key="t150" label={getHeaderLabel(sheetType, "tray150mm", "150mm")} keyName="tray150mm" onRemove={() => removeColumn("tray150mm")} updateCustomHeader={updateCustomHeader} />);
 if (isColActive("tray100mm")) cells.push(<EditableHeader key="t100" label={getHeaderLabel(sheetType, "tray100mm", "100mm")} keyName="tray100mm" onRemove={() => removeColumn("tray100mm")} updateCustomHeader={updateCustomHeader} />);
 }
 }
 for (let num = 1; num <= 20; num++) {
 const colKey = `col${num}`;
 if (!isColActive(colKey)) continue;

 // Check if this column is part of a group (either as parent or child)
 let parentKey = null;
 for (let i = 1; i <= num; i++) {
 const pKey = `col${i}`;
 const pSettings = formData.columnSettings?.[pKey];
 if (isColActive(pKey) && pSettings && (pSettings.colSpan > 1 || pSettings.isGroupParent)) {
 const pSpan = pSettings.colSpan || 1;
 if (num >= i && num < i + pSpan) {
 parentKey = pKey;
 break;
 }
 }
 }

 if (parentKey) {
 cells.push(
 <EditableHeader 
 key={`sub-${colKey}`} 
 label={getHeaderLabel(sheetType, colKey, "Sub")} 
 keyName={colKey} 
 onRemove={() => removeColumn(colKey)} 
 updateCustomHeader={updateCustomHeader} 
 />
 );
 }
 }
 return cells;
 };

 const renderBodyRow = (row, idx) => {
 const { sheetType } = formData;
 const cells = [];
 if (formData.boqItem && formData.boqItem.length > 1) {
 cells.push(
 <td key="boqSelect">
 <Form.Select 
 style={inputStyle} 
 value={row.boqItemId || ""} 
 onChange={e => handleRowChange(idx, "boqItemId", e.target.value)}
 >
 <option value="">Select BOQ</option>
 {formData.boqItem.map(id => {
 const b = boqList.find(x => x._id === id);
 const remainingQty = getBoqRemainingQty(id);
 const isFull = remainingQty <= 0 && row.boqItemId !== id;
 return b ? <option key={id} value={id} disabled={isFull}>{b.wlino} - {b.itemName}{remainingQty <= 0 ? " (No balance)" : ` (${remainingQty.toFixed(2)} left)`}</option> : null;
 })}
 </Form.Select>
 </td>
 );
 }
 if (isColActive("srNo")) cells.push(<td key="sr" className="text-center fw-bold">{idx + 1}</td>);
 if (isColActive("location")) cells.push(<td key="loc"><Form.Control style={inputStyle} value={row.location || ""} onChange={e => handleRowChange(idx, "location", e.target.value)} /></td>);
 if (isColActive("areaNo")) cells.push(<td key="areaN"><Form.Control style={inputStyle} value={row.areaNo || ""} onChange={e => handleRowChange(idx, "areaNo", e.target.value)} /></td>);
 if (isColActive("uom")) cells.push(<td key="uom"><Form.Select style={inputStyle} value={row.uom || ""} onChange={e => handleRowChange(idx, "uom", e.target.value)}><option value="">-</option>{uomList.map(u => <option key={u._id} value={u.name}>{u.name}</option>)}</Form.Select></td>);
 
 if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("from")) cells.push(<td key="from"><Form.Control style={inputStyle} value={row.pointFrom || ""} onChange={e => handleRowChange(idx, "pointFrom", e.target.value)} /></td>);
 if (isColActive("to")) cells.push(<td key="to"><Form.Control style={inputStyle} value={row.pointTo || ""} onChange={e => handleRowChange(idx, "pointTo", e.target.value)} /></td>);
 }
 
 if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("from")) cells.push(<td key="from"><Form.Control style={inputStyle} value={row.cableFrom || ""} onChange={e => handleRowChange(idx, "cableFrom", e.target.value)} /></td>);
 if (isColActive("to")) cells.push(<td key="to"><Form.Control style={inputStyle} value={row.cableTo || ""} onChange={e => handleRowChange(idx, "cableTo", e.target.value)} /></td>);
 }
 
 if (["ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType)) {
 if (!["CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType) && isColActive("wireSize")) cells.push(<td key="wire"><Form.Control style={inputStyle} value={row.wireSize || ""} onChange={e => handleRowChange(idx, "wireSize", e.target.value)} /></td>);
 if (isColActive("circuitNo")) cells.push(<td key="circ"><Form.Control style={inputStyle} value={row.circuitNumber || ""} onChange={e => handleRowChange(idx, "circuitNumber", e.target.value)} /></td>);
 }
 
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("conduitSize")) cells.push(<td key="conduit"><Form.Control style={inputStyle} value={row.conduitSize || ""} onChange={e => handleRowChange(idx, "conduitSize", e.target.value)} /></td>);
 if (isColActive("pointHeader")) {
 if (isColActive("pointFrom")) cells.push(<td key="pf"><Form.Control style={inputStyle} value={row.pointFrom || ""} onChange={e => handleRowChange(idx, "pointFrom", e.target.value)} /></td>);
 if (isColActive("pointTo")) cells.push(<td key="pt"><Form.Control style={inputStyle} value={row.pointTo || ""} onChange={e => handleRowChange(idx, "pointTo", e.target.value)} /></td>);
 if (isColActive("pointQty")) cells.push(<td key="pq"><Form.Control style={inputStyle} type="number" value={row.pointQty || 0} onChange={e => handleRowChange(idx, "pointQty", e.target.value)} /></td>);
 }
 if (isColActive("powerHeader")) {
 if (isColActive("powerFrom")) cells.push(<td key="pwf"><Form.Control style={inputStyle} value={row.powerFrom || ""} onChange={e => handleRowChange(idx, "powerFrom", e.target.value)} /></td>);
 if (isColActive("powerTo")) cells.push(<td key="pwt"><Form.Control style={inputStyle} value={row.powerTo || ""} onChange={e => handleRowChange(idx, "powerTo", e.target.value)} /></td>);
 if (isColActive("powerQty")) cells.push(<td key="pwq"><Form.Control style={inputStyle} type="number" value={row.powerQty || 0} onChange={e => handleRowChange(idx, "powerQty", e.target.value)} /></td>);
 }
 if (isColActive("subMainHeader")) {
 if (isColActive("subMainFrom")) cells.push(<td key="smf"><Form.Control style={inputStyle} value={row.subMainFrom || ""} onChange={e => handleRowChange(idx, "subMainFrom", e.target.value)} /></td>);
 if (isColActive("subMainTo")) cells.push(<td key="smt"><Form.Control style={inputStyle} value={row.subMainTo || ""} onChange={e => handleRowChange(idx, "subMainTo", e.target.value)} /></td>);
 if (isColActive("subMainQty")) cells.push(<td key="smq"><Form.Control style={inputStyle} type="number" value={row.subMainQty || 0} onChange={e => handleRowChange(idx, "subMainQty", e.target.value)} /></td>);
 }
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) {
 if (isColActive("dbToSb")) cells.push(<td key="dbsb"><Form.Control style={inputStyle} type="number" value={row.primaryPointDbToSb || 0} onChange={e => handleRowChange(idx, "primaryPointDbToSb", e.target.value)} /></td>);
 if (isColActive("dbTo1stLight")) cells.push(<td key="db1l"><Form.Control style={inputStyle} type="number" value={row.primaryPointDbTo1stLight || 0} onChange={e => handleRowChange(idx, "primaryPointDbTo1stLight", e.target.value)} /></td>);
 if (isColActive("sbTo1stPoint")) cells.push(<td key="sb1p"><Form.Control style={inputStyle} type="number" value={row.primaryPointSbTo1stPoint || 0} onChange={e => handleRowChange(idx, "primaryPointSbTo1stPoint", e.target.value)} /></td>);
 }
 if (isColActive("secondaryHeader")) {
 if (isColActive("sbToSb")) cells.push(<td key="sbsb"><Form.Control style={inputStyle} type="number" value={row.secondaryPointSbToSb || 0} onChange={e => handleRowChange(idx, "secondaryPointSbToSb", e.target.value)} /></td>);
 if (isColActive("pointToPoint")) cells.push(<td key="ptp"><Form.Control style={inputStyle} type="number" value={row.secondaryPointPointToPoint || 0} onChange={e => handleRowChange(idx, "secondaryPointPointToPoint", e.target.value)} /></td>);
 }
 if (isColActive("primaryPowerHeader")) cells.push(<td key="prip"><Form.Control style={inputStyle} type="number" value={row.primaryPowerPoints || 0} onChange={e => handleRowChange(idx, "primaryPowerPoints", e.target.value)} /></td>);
 if (isColActive("secondaryPowerHeader")) cells.push(<td key="secp"><Form.Control style={inputStyle} type="number" value={row.secondaryPowerPoints || 0} onChange={e => handleRowChange(idx, "secondaryPowerPoints", e.target.value)} /></td>);
 if (sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("fanFixingHeader")) cells.push(<td key="fan"><Form.Control style={inputStyle} type="number" value={row.fanFixing || 0} onChange={e => handleRowChange(idx, "fanFixing", e.target.value)} /></td>);
 if (isColActive("lightFixingHeader")) cells.push(<td key="light"><Form.Control style={inputStyle} type="number" value={row.lightFixing || 0} onChange={e => handleRowChange(idx, "lightFixing", e.target.value)} /></td>);
 }
 }
 
 const handledCols = new Set();
 if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) {
 if (isColActive("col1")) { cells.push(<td key="c20"><Form.Control style={inputStyle} type={formData.columnSettings?.col1?.fieldType || "number"} value={row.col1 || 0} onChange={e => handleRowChange(idx, "col1", e.target.value)} /></td>); handledCols.add("col1"); }
 if (isColActive("col2")) { cells.push(<td key="c25"><Form.Control style={inputStyle} type={formData.columnSettings?.col2?.fieldType || "number"} value={row.col2 || 0} onChange={e => handleRowChange(idx, "col2", e.target.value)} /></td>); handledCols.add("col2"); }
 if (isColActive("col3")) { cells.push(<td key="c32"><Form.Control style={inputStyle} type={formData.columnSettings?.col3?.fieldType || "number"} value={row.col3 || 0} onChange={e => handleRowChange(idx, "col3", e.target.value)} /></td>); handledCols.add("col3"); }
 }
 if (isColActive("elvConduitHeader")) {
 if (isColActive("col4")) { cells.push(<td key="e20"><Form.Control style={inputStyle} type={formData.columnSettings?.col4?.fieldType || "number"} value={row.col4 || 0} onChange={e => handleRowChange(idx, "col4", e.target.value)} /></td>); handledCols.add("col4"); }
 if (isColActive("col5")) { cells.push(<td key="e25"><Form.Control style={inputStyle} type={formData.columnSettings?.col5?.fieldType || "number"} value={row.col5 || 0} onChange={e => handleRowChange(idx, "col5", e.target.value)} /></td>); handledCols.add("col5"); }
 if (isColActive("col6")) { cells.push(<td key="e32"><Form.Control style={inputStyle} type={formData.columnSettings?.col6?.fieldType || "number"} value={row.col6 || 0} onChange={e => handleRowChange(idx, "col6", e.target.value)} /></td>); handledCols.add("col6"); }
 }
 } else if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("cableRunHeader")) cells.push(<td key="crun"><Form.Control style={inputStyle} type="number" value={row.cableRun || 0} onChange={e => handleRowChange(idx, "cableRun", e.target.value)} /></td>);
 if (isColActive("cableQtyHeader")) cells.push(<td key="cqty"><Form.Control style={inputStyle} type="number" value={row.cableQty || 0} onChange={e => handleRowChange(idx, "cableQty", e.target.value)} /></td>);
 if (isColActive("totalCablesHeader")) cells.push(<td key="ctot" className="text-center fw-bold">{(Number(row.cableRun || 0) * Number(row.cableQty || 0)).toFixed(2)}</td>);
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("lengthHeader")) cells.push(<td key="len"><Form.Control style={inputStyle} type="number" value={row.length || 0} onChange={e => handleRowChange(idx, "length", e.target.value)} /></td>);
 if (isColActive("runHeader")) cells.push(<td key="run"><Form.Control style={inputStyle} type="number" value={row.run || 0} onChange={e => handleRowChange(idx, "run", e.target.value)} /></td>);
 if (isColActive("perforated1Header")) {
 if (isColActive("tray300mm")) cells.push(<td key="t300"><Form.Control style={inputStyle} type="number" value={row.tray300mm || 0} onChange={e => handleRowChange(idx, "tray300mm", e.target.value)} /></td>);
 if (isColActive("tray450mm")) cells.push(<td key="t450"><Form.Control style={inputStyle} type="number" value={row.tray450mm || 0} onChange={e => handleRowChange(idx, "tray450mm", e.target.value)} /></td>);
 if (isColActive("tray600mm")) cells.push(<td key="t600"><Form.Control style={inputStyle} type="number" value={row.tray600mm || 0} onChange={e => handleRowChange(idx, "tray600mm", e.target.value)} /></td>);
 }
 if (isColActive("ladderHeader")) {
 if (isColActive("ladder900mm")) cells.push(<td key="l900"><Form.Control style={inputStyle} type="number" value={row.ladder900mm || 0} onChange={e => handleRowChange(idx, "ladder900mm", e.target.value)} /></td>);
 if (isColActive("ladder1200mm")) cells.push(<td key="l1200"><Form.Control style={inputStyle} type="number" value={row.ladder1200mm || 0} onChange={e => handleRowChange(idx, "ladder1200mm", e.target.value)} /></td>);
 }
 if (isColActive("stripHeader")) {
 if (isColActive("strip25x3")) cells.push(<td key="s25"><Form.Control style={inputStyle} type="number" value={row.strip25x3 || 0} onChange={e => handleRowChange(idx, "strip25x3", e.target.value)} /></td>);
 if (isColActive("strip32x6")) cells.push(<td key="s32"><Form.Control style={inputStyle} type="number" value={row.strip32x6 || 0} onChange={e => handleRowChange(idx, "strip32x6", e.target.value)} /></td>);
 }
 if (isColActive("perforated2Header")) {
 if (isColActive("tray150mm")) cells.push(<td key="t150"><Form.Control style={inputStyle} type="number" value={row.tray150mm || 0} onChange={e => handleRowChange(idx, "tray150mm", e.target.value)} /></td>);
 if (isColActive("tray100mm")) cells.push(<td key="t100"><Form.Control style={inputStyle} type="number" value={row.tray100mm || 0} onChange={e => handleRowChange(idx, "tray100mm", e.target.value)} /></td>);
 }
 }
 for (let num = 1; num <= 20; num++) {
 const colKey = `col${num}`;
 if (isColActive(colKey) && !handledCols.has(colKey)) {
 const fieldType = formData.columnSettings?.[colKey]?.fieldType || "number";
 cells.push(
 <td key={colKey}>
 <Form.Control 
 style={inputStyle} 
 type={fieldType === "number" ? "number" : "text"} 
 value={row[colKey] || (fieldType === "number" ? 0 : "")} 
 onChange={e => handleRowChange(idx, colKey, e.target.value)} 
 />
 </td>
 );
 }
 }
 if (isColActive("remarks")) cells.push(<td key="rem"><Form.Control style={inputStyle} value={row.remarks || ""} onChange={e => handleRowChange(idx, "remarks", e.target.value)} /></td>);
 if (isColActive("action")) cells.push(<td key="act" className="text-center"><Button variant="danger" size="sm" className="btn-xs" onClick={() => removeRow(idx)} disabled={rows.length === 1}><i className="fa fa-trash"></i></Button></td>);
 return cells;
 };

 const renderTotalsRow = () => {
 const { sheetType } = formData;
 const cells = [];
 if (formData.boqItem && formData.boqItem.length > 1) {
 cells.push(<td key="t-boq" className="bg-warning"></td>);
 }
 const baseCols = ["srNo", "location", "areaNo", "uom"];
 const activeBaseCount = baseCols.filter(c => isColActive(c)).length;
 if (activeBaseCount > 0) cells.push(<td key="t-base" colSpan={activeBaseCount} className="text-center py-2 fw-bold bg-warning">TOTAL</td>);
 
 if (sheetType === "CONDUIT_SUMMARY" || sheetType === "CABLE_MEASUREMENT") {
 const count = (isColActive("from") ? 1 : 0) + (isColActive("to") ? 1 : 0);
 if (count > 0) cells.push(<td key="t-from-to" colSpan={count} className="bg-warning"></td>);
 }
 
 if (["ELECTRICAL_LIGHTING", "CONDUIT_MEASUREMENT", "CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType)) {
 if (!["CABLE_MEASUREMENT", "WIRING_MEASUREMENT"].includes(sheetType) && isColActive("wireSize")) cells.push(<td key="t-wire" className="bg-warning"></td>);
 if (isColActive("circuitNo")) cells.push(<td key="t-circ" className="bg-warning"></td>);
 }
 
 if (sheetType === "ELECTRICAL_LIGHTING") {
 if (isColActive("conduitSize")) cells.push(<td key="t-conduit" className="bg-warning"></td>);
 if (isColActive("pointHeader")) {
 const count = (isColActive("pointFrom") ? 1 : 0) + (isColActive("pointTo") ? 1 : 0);
 if (count > 0) cells.push(<td key="t-pft" colSpan={count} className="bg-warning"></td>);
 if (isColActive("pointQty")) cells.push(<td key="t-pq" className="text-center fw-bold bg-warning">{totals.pointQty}</td>);
 }
 if (isColActive("powerHeader")) {
 const count = (isColActive("powerFrom") ? 1 : 0) + (isColActive("powerTo") ? 1 : 0);
 if (count > 0) cells.push(<td key="t-pwft" colSpan={count} className="bg-warning"></td>);
 if (isColActive("powerQty")) cells.push(<td key="t-pwq" className="text-center fw-bold bg-warning">{totals.powerQty}</td>);
 }
 if (isColActive("subMainHeader")) {
 const count = (isColActive("subMainFrom") ? 1 : 0) + (isColActive("subMainTo") ? 1 : 0);
 if (count > 0) cells.push(<td key="t-smft" colSpan={count} className="bg-warning"></td>);
 if (isColActive("subMainQty")) cells.push(<td key="t-smq" className="text-center fw-bold bg-warning">{totals.subMainQty}</td>);
 }
 } else if (sheetType === "CONDUIT_MEASUREMENT" || sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("primaryHeader")) {
 if (isColActive("dbToSb")) cells.push(<td key="t-dbsb" className="text-center fw-bold bg-warning">{totals.primaryPointDbToSb}</td>);
 if (isColActive("dbTo1stLight")) cells.push(<td key="t-db1l" className="text-center fw-bold bg-warning">{totals.primaryPointDbTo1stLight}</td>);
 if (isColActive("sbTo1stPoint")) cells.push(<td key="t-sb1p" className="text-center fw-bold bg-warning">{totals.primaryPointSbTo1stPoint}</td>);
 }
 if (isColActive("secondaryHeader")) {
 if (isColActive("sbToSb")) cells.push(<td key="t-sbsb" className="text-center fw-bold bg-warning">{totals.secondaryPointSbToSb}</td>);
 if (isColActive("pointToPoint")) cells.push(<td key="t-ptp" className="text-center fw-bold bg-warning">{totals.secondaryPointPointToPoint}</td>);
 }
 if (isColActive("primaryPowerHeader")) cells.push(<td key="t-prip" className="text-center fw-bold bg-warning">{totals.primaryPowerPoints}</td>);
 if (isColActive("secondaryPowerHeader")) cells.push(<td key="t-secp" className="text-center fw-bold bg-warning">{totals.secondaryPowerPoints}</td>);
 if (sheetType === "WIRING_MEASUREMENT") {
 if (isColActive("fanFixingHeader")) cells.push(<td key="t-fan" className="text-center fw-bold bg-warning">{totals.fanFixing}</td>);
 if (isColActive("lightFixingHeader")) cells.push(<td key="t-light" className="text-center fw-bold bg-warning">{totals.lightFixing}</td>);
 }
 } else if (sheetType === "CONDUIT_SUMMARY") {
 if (isColActive("conduitHeader")) {
 if (isColActive("col1")) cells.push(<td key="t-c20" className="text-center fw-bold bg-warning">{totals.conduit20mm}</td>);
 if (isColActive("col2")) cells.push(<td key="t-c25" className="text-center fw-bold bg-warning">{totals.conduit25mm}</td>);
 if (isColActive("col3")) cells.push(<td key="t-c32" className="text-center fw-bold bg-warning">{totals.conduit32mm}</td>);
 }
 if (isColActive("elvConduitHeader")) {
 if (isColActive("col4")) cells.push(<td key="t-e20" className="text-center fw-bold bg-warning">{totals.elvConduit20mm}</td>);
 if (isColActive("col5")) cells.push(<td key="t-e25" className="text-center fw-bold bg-warning">{totals.elvConduit25mm}</td>);
 if (isColActive("col6")) cells.push(<td key="t-e32" className="text-center fw-bold bg-warning">{totals.elvConduit32mm}</td>);
 }
 } else if (sheetType === "CABLE_MEASUREMENT") {
 if (isColActive("cableRunHeader")) cells.push(<td key="t-crun" className="bg-warning"></td>);
 if (isColActive("cableQtyHeader")) cells.push(<td key="t-cqty" className="bg-warning"></td>);
 if (isColActive("totalCablesHeader")) cells.push(<td key="t-ctot" className="text-center fw-bold bg-warning">{totals.totalCables}</td>);
 } else if (sheetType === "CABLE_TRAY_SUMMARY") {
 if (isColActive("lengthHeader")) cells.push(<td key="t-len" className="bg-warning"></td>);
 if (isColActive("runHeader")) cells.push(<td key="t-run" className="bg-warning"></td>);
 if (isColActive("perforated1Header")) {
 if (isColActive("tray300mm")) cells.push(<td key="t-t300" className="text-center fw-bold bg-warning">{totals.tray300mm}</td>);
 if (isColActive("tray450mm")) cells.push(<td key="t-t450" className="text-center fw-bold bg-warning">{totals.tray450mm}</td>);
 if (isColActive("tray600mm")) cells.push(<td key="t-t600" className="text-center fw-bold bg-warning">{totals.tray600mm}</td>);
 }
 if (isColActive("ladderHeader")) {
 if (isColActive("ladder900mm")) cells.push(<td key="t-l900" className="text-center fw-bold bg-warning">{totals.ladder900mm}</td>);
 if (isColActive("ladder1200mm")) cells.push(<td key="t-l1200" className="text-center fw-bold bg-warning">{totals.ladder1200mm}</td>);
 }
 if (isColActive("stripHeader")) {
 if (isColActive("strip25x3")) cells.push(<td key="t-s25" className="text-center fw-bold bg-warning">{totals.strip25x3}</td>);
 if (isColActive("strip32x6")) cells.push(<td key="t-s32" className="text-center fw-bold bg-warning">{totals.strip32x6}</td>);
 }
 if (isColActive("perforated2Header")) {
 if (isColActive("tray150mm")) cells.push(<td key="t-t150" className="text-center fw-bold bg-warning">{totals.tray150mm}</td>);
 if (isColActive("tray100mm")) cells.push(<td key="t-t100" className="text-center fw-bold bg-warning">{totals.tray100mm}</td>);
 }
 }
 for (let num = 1; num <= 20; num++) {
 const colKey = `col${num}`;
 if (isColActive(colKey)) {
 const fieldType = formData.columnSettings?.[colKey]?.fieldType || "number";
 cells.push(<td key={`t-${colKey}`} className="text-center fw-bold bg-warning">{fieldType === "number" ? totals[colKey] : ""}</td>);
 }
 }
 if (isColActive("remarks")) cells.push(<td key="t-rem" className="bg-warning"></td>);
 if (isColActive("action")) cells.push(<td key="t-act" className="bg-warning"></td>);
 return cells;
 };

 return (
 <div className="h-100">
 <PageTitle activeMenu="Add Measurement Sheet" motherMenu="Measurement Sheets" />
 <Row>
 <Col xl={12}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
 <div className="d-flex align-items-center gap-3">
 <h4 className="mb-0 fw-bold">New Measurement Sheet</h4>
 {isAutoSaving && (
 <Badge bg="light" className="text-muted border d-flex align-items-center gap-2" style={{ fontSize: "11px" }}>
 <div className="spinner-border spinner-border-sm" style={{ width: "10px", height: "10px" }}></div>
 Auto-saving draft...
 </Badge>
 )}
 {savedId && !isAutoSaving && (
 <Badge bg="success-light" className="text-success border" style={{ fontSize: "11px" }}>
 <i className="fa fa-check-circle me-1"></i> Draft Saved
 </Badge>
 )}
 </div>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 <Row className="mb-4 g-3">
 <Col md={4}>
 <Form.Group>
 <Form.Label className="fw-bold small text-muted">Work Order *</Form.Label>
 <Select 
 options={workOrders.map((w) => {
 const woNo = w.woNo || w.woNumber || "N/A";
 const woLabel = w.label ? ` – ${w.label}` : "";
 const projectName = w.projectName || w.project?.projectName || w.project?.name || "";
 return { 
 value: w._id, 
 label: `${woNo}${woLabel}${projectName ? ` - ${projectName}` : ""}` 
 };
 })} 
 value={(() => {
 const wo = workOrders.find((w) => w._id === formData.workOrder);
 if (!wo) return null;
 const woNo = wo.woNo || wo.woNumber || "N/A";
 const woLabel = wo.label ? ` – ${wo.label}` : "";
 const projectName = wo.projectName || wo.project?.projectName || wo.project?.name || "";
 return { 
 value: formData.workOrder, 
 label: `${woNo}${woLabel}${projectName ? ` - ${projectName}` : ""}` 
 };
 })()}
 onChange={handleWorkOrderChange} 
 styles={selectStyles}
 menuPortalTarget={document.body}
 />
 </Form.Group>
 </Col>
 <Col md={4}><Form.Group><Form.Label className="fw-bold small text-muted">BOQ Item *</Form.Label><Select isMulti options={boqList.map((b) => ({ value: b._id, label: `${b.wlino} - ${b.itemName}` }))} value={(() => {
 const items = Array.isArray(formData.boqItem) ? formData.boqItem : (formData.boqItem ? [formData.boqItem] : []);
 return items.map(id => {
 const b = boqList.find(x => x._id === id);
 return b ? { value: b._id, label: `${b.wlino} - ${b.itemName}` } : null;
 }).filter(Boolean);
 })()} onChange={handleBOQChange} styles={selectStyles} menuPortalTarget={document.body} /></Form.Group></Col>
 <Col md={4}><Form.Group><Form.Label className="fw-bold small text-muted">Sheet Type *</Form.Label><Form.Select value={formData.sheetType} onChange={(e) => handleSheetTypeChange(e.target.value)}>{sheetTypeMasters.map((type) => <option key={type._id || type.code} value={type.code}>{type.name?.toUpperCase() || type.code}</option>)}</Form.Select></Form.Group></Col>
 </Row>
 <Row className="mb-4 g-3">
 <Col md={4}><Form.Label className="fw-bold small">Location</Form.Label><Form.Control value={formData.location} onChange={e => setFormData(p => ({ ...p, location: e.target.value }))} /></Col>
 <Col md={4}><Form.Label className="fw-bold small">Date</Form.Label><Form.Control type="date" value={formData.preparedDate} onChange={e => setFormData(p => ({ ...p, preparedDate: e.target.value }))} /></Col>
 <Col md={4} className="d-flex align-items-end"><Button variant="outline-primary" className="w-100 fw-bold" onClick={openGroupModal}><i className="fa fa-plus-circle me-2"></i>Add Column Group</Button></Col>
 </Row>
 <div className="table-responsive" style={{ border: "1px solid #000", borderRadius: "4px", overflow: "visible" }}>
 <Table bordered hover responsive className="mb-0" style={{ minWidth: "1800px", tableLayout: "fixed" }}>
 <thead>
 <tr className="border-bottom-2 border-dark">{renderRow1()}</tr>
 <tr className="border-bottom-2 border-dark">{renderRow2()}</tr>
 </thead>
 <tbody>{rows.map((row, idx) => <tr key={idx}>{renderBodyRow(row, idx)}</tr>)}</tbody>
 <tfoot><tr className="bg-warning fw-bold sticky-bottom border-top-2 border-dark">{renderTotalsRow()}</tr></tfoot>
 </Table>
 </div>
 <div className="mt-4 d-flex justify-content-between align-items-center">
 <Button variant="info" className="fw-bold text-white" onClick={addRow}><i className="fa fa-plus me-2"></i>Add New Row</Button>
 <div className="d-flex gap-2">
 <Button variant="danger" onClick={() => navigate(-1)} className="fw-bold">Cancel</Button>
 <Button variant="primary" type="submit" className="fw-bold px-5" disabled={loading}>{loading ? "Saving..." : "Save Measurement Sheet"}</Button>
 </div>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 <Modal show={showModal} onHide={() => setShowModal(false)} centered>
 <Modal.Header closeButton><Modal.Title className="fw-bold">Add New Column(s)</Modal.Title></Modal.Header>
 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label className="fw-bold small">Heading Name *</Form.Label>
 <Form.Control 
 placeholder="e.g. LIGHT POINTS" 
 value={modalData.parent} 
 onChange={e => setModalData({ ...modalData, parent: e.target.value })} 
 />
 <Form.Text className="text-muted">This will be the main heading for your column(s).</Form.Text>
 </Form.Group>
 
 <Form.Group className="mb-3">
 <Form.Label className="fw-bold small">Input Type *</Form.Label>
 <div className="d-flex gap-4">
 <Form.Check 
 type="radio" 
 label="Number (e.g. 10, 20.5)" 
 name="fieldType" 
 checked={modalData.fieldType === "number"} 
 onChange={() => setModalData({ ...modalData, fieldType: "number" })} 
 />
 <Form.Check 
 type="radio" 
 label="Text (e.g. Remarks, Names)" 
 name="fieldType" 
 checked={modalData.fieldType === "text"} 
 onChange={() => setModalData({ ...modalData, fieldType: "text" })} 
 />
 </div>
 </Form.Group>

 <hr />

 <Form.Group className="mb-2">
 <Form.Label className="fw-bold small d-flex justify-content-between align-items-center">
 Sub-Headings (Optional)
 <Badge bg="info-light" className="text-info fw-normal">Leave empty for single column</Badge>
 </Form.Label>
 {modalData.subs.map((s, idx) => (
 <div key={idx} className="d-flex gap-2 mb-2">
 <Form.Control 
 size="sm" 
 placeholder={`Sub-heading ${idx + 1}`} 
 value={s} 
 onChange={e => { 
 const newSubs = [...modalData.subs]; 
 newSubs[idx] = e.target.value; 
 setModalData({ ...modalData, subs: newSubs }); 
 }} 
 />
 <Button 
 variant="outline-danger" 
 size="sm" 
 onClick={() => setModalData({ ...modalData, subs: modalData.subs.filter((_, i) => i !== idx) })} 
 disabled={modalData.subs.length === 1 && !modalData.subs[0]}
 >
 <i className="fa fa-times"></i>
 </Button>
 </div>
 ))}
 <Button 
 variant="outline-primary" 
 size="sm" 
 className="mt-1" 
 onClick={() => setModalData({ ...modalData, subs: [...modalData.subs, ""] })}
 >
 <i className="fa fa-plus me-1"></i>Add Another Sub-heading
 </Button>
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={handleModalSave} className="px-4">Create Column(s)</Button>
 </Modal.Footer>
 </Modal>
 <style>{`
 .header-editable-input { transition: all 0.2s; }
 .header-editable-input:hover { background-color: rgba(0,0,0,0.05) !important; }
 .header-editable-input:focus { background-color: #fff !important; box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25) !important; }
 .remove-header-btn:hover { transform: translateY(-50%) scale(1.2) !important; color: #ff0000 !important; }
 .group-header-container:hover .remove-header-btn { opacity: 1 !important; }
 .remove-header-btn { opacity: 0.6; transition: all 0.2s; }
 table th, table td { border: 1px solid #000 !important; }
 .table-responsive { max-height: 600px; overflow-y: auto; }
 thead th { position: sticky; top: 0; background: #92D050 !important; z-index: 10; border-bottom: 2px solid #000 !important; }
 tfoot td { position: sticky; bottom: 0; background: #ffc107 !important; z-index: 5; border-top: 2px solid #000 !important; }
 `}</style>
 </div>
 );
};

export default MeasurementSheetAdd;
